import * as Yup from "yup";
import { UserRole, UserRoles } from "../../../constants/roles";

export const userFormValidationSchema = Yup.object({
  // User Information
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  role: Yup.string()
    .required("Role is required")
    .oneOf(["Admin", "Dispatcher", "TruckDriver", "Fleet", "Safety", "Recruiting", "Accounting", "IftaAdmin"], "Invalid role selected"),

  // Employee Information
  isEmployee: Yup.boolean(),
  employmentStartDate: Yup.string().when("isEmployee", {
    is: true,
    then: (schema) => schema.required("Employment start date is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  employmentEndDate: Yup.string().nullable(),
  dateOfBirth: Yup.string().nullable(),
  familyHoliday: Yup.string()
    .nullable()
    .matches(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/, "Invalid format. Use MM-DD (e.g., 12-19)"),
  phoneNumber: Yup.string()
    .nullable()
    .max(50, "Phone number cannot exceed 50 characters"),

  // Address Information (required when isEmployee is true)
  address: Yup.object().when("isEmployee", {
    is: true,
    then: (schema) => schema.shape({
      street: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zipCode: Yup.string().nullable(),
      country: Yup.string().nullable(),
    }),
    otherwise: (schema) => schema.shape({
      street: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string().nullable(),
      zipCode: Yup.string().nullable(),
      country: Yup.string().nullable(),
    }),
  }),

  // Emergency Contact (required when isEmployee is true)
  emergencyContact: Yup.object().when("isEmployee", {
    is: true,
    then: (schema) => schema.shape({
      name: Yup.string().nullable(),
      phone: Yup.string().nullable(),
    }),
    otherwise: (schema) => schema.shape({
      name: Yup.string().nullable(),
      phone: Yup.string().nullable(),
    }),
  }),

  // Driver Information (required when role is TruckDriver and isEmployee is true)
  driverInfo: Yup.object().when(["role", "isEmployee"], {
    is: (role: UserRole, isEmployee: boolean) => {
      console.log("Driver validation check:", {
        role,
        isEmployee,
        isTruckDriver: role === UserRoles.TRUCK_DRIVER,
        truckDriverConstant: UserRoles.TRUCK_DRIVER,
        roleType: typeof role,
        constantType: typeof UserRoles.TRUCK_DRIVER
      });
      return role === UserRoles.TRUCK_DRIVER && isEmployee;
    },
    then: (schema) => schema.shape({
      companyName: Yup.string().nullable(),
      ein: Yup.string().nullable(),
      ssn: Yup.string().nullable(),
    }).test(
      "ein-or-ssn-required",
      "Either EIN or SSN must be provided for drivers",
      function (value) {
        const { ein, ssn } = (value as any) || {};
        const hasEin = typeof ein === 'string' && ein.trim().length > 0;
        const hasSsn = typeof ssn === 'string' && ssn.trim().length > 0;
        const isValid = hasEin || hasSsn;
        console.log("EIN/SSN validation:", { ein, ssn, hasEin, hasSsn, isValid });
        if (isValid) return true;
        // Attach the error to EIN field so it shows under one of the inputs
        return this.createError({ path: `${this.path}.ein`, message: "Either EIN or SSN must be provided for drivers" });
      }
    ),
    otherwise: (schema) => schema.shape({
      companyName: Yup.string().nullable(),
      ein: Yup.string().nullable(),
      ssn: Yup.string().nullable(),
    }),
  }),
});

export const getInitialUserFormValues = (): any => ({
  // User Information
  firstName: "",
  lastName: "",
  email: "",
  role: UserRoles.DISPATCHER,

  // Employee Information
  isEmployee: true,
  employmentStartDate: null,
  employmentEndDate: null,
  dateOfBirth: null,
  familyHoliday: null,
  worksWeekends: false,
  phoneNumber: "",

  // Address Information
  address: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },

  // Emergency Contact
  emergencyContact: {
    name: "",
    phone: "",
  },

  // Driver Information
  driverInfo: {
    companyName: "",
    ein: "",
    ssn: "",
  },
});
